/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';

import { IAllowableFreightData } from './allowable-freight.data';
import { IAllowableFreightProps } from './allowable-freight.props.autogenerated';
import { AllowableFreightStatusType } from './utils/AllowableFreightStatusEnum';
import { AllowableFreightProgressBarClassEnum } from './utils/AllowableFreightProgressBarEnum';
import { reaction } from 'mobx';
import _ from 'lodash';
import { IAllowableFreightCalculation, IAllowableFreightStatusLine, IProductCodeEntity } from '../../actions/DataServiceEntities.g';

import getAllowableFreightCalculationAction, {
    GetAllowableFreightCalculationInput
} from '../../actions/get-allowable-freight-calculation.action';
import { CartLine } from '@msdyn365-commerce/retail-proxy';
import { getProductCodesAsync } from '../../actions/DataActionExtension.g';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
export interface IAllowableFreightViewProps extends IAllowableFreightProps<IAllowableFreightData> {
    state: AllowableFreightState;
}
export interface AllowableFreightState {
    containsOil: boolean;
    oversizedItem: boolean;
    mustShipLTL: boolean;
    limitReached: boolean;
    cartFreight: number;
    sliderColourClass: string;
    errorMessage: string | undefined;
    maxLimit: number;
    isUnitCount: boolean;
    cartHasSpringPromoCoupon: boolean;
    productCodes: IProductCodeEntity[];
}

/**
 *
 * AllowableFreight component
 * @extends {React.PureComponent<IAllowableFreightProps<IAllowableFreightData>>}
 */
class AllowableFreight extends React.PureComponent<IAllowableFreightProps<IAllowableFreightData>, AllowableFreightState> {
    constructor(props: IAllowableFreightProps<IAllowableFreightData>) {
        super(props);
        this.state = {
            containsOil: false,
            oversizedItem: false,
            mustShipLTL: false,
            limitReached: false,
            cartFreight: 0,
            sliderColourClass: AllowableFreightProgressBarClassEnum.Red,
            errorMessage: undefined,
            maxLimit: 0,
            isUnitCount: false,
            cartHasSpringPromoCoupon: false,
            productCodes: []
        };
    }
    public componentDidMount() {
        if (this.props.data.cart.result?.cart) {
            const {
                data: {
                    cart: {
                        result: {
                            cart: { Id, CartLines }
                        }
                    }
                }
            } = this.props;

            this.setState({ cartHasSpringPromoCoupon: this.cartHasSpringPromoCoupons() });
            void this.updateAllowableFreightv2(Id, CartLines || []);
            void this.getProductCodesForCartLines();
        }

        reaction(
            () => this.props.data.cart.result?.cart,
            () => {
                if (this.props.data.cart.result?.cart) {
                    const {
                        data: {
                            cart: {
                                result: {
                                    cart: { Id, CartLines }
                                }
                            }
                        }
                    } = this.props;
                    this.setState({ cartHasSpringPromoCoupon: this.cartHasSpringPromoCoupons() });
                    // this.updateAllowableFreight(this.props.data.cart.result!.cart);
                    void this.updateAllowableFreightv2(Id, CartLines || []);
                    void this.getProductCodesForCartLines();
                }
            }
        );
    }

    public cartHasSpringPromoCoupons = () => {
        if (this.props.data.cart.result) {
            const {
                data: {
                    cart: {
                        result: {
                            cart: { Coupons }
                        }
                    }
                },
                config: { springPromoCoupons }
            } = this.props;
            if (ArrayExtensions.hasElements(springPromoCoupons) && ArrayExtensions.hasElements(Coupons)) {
                const items = _.intersection(
                    springPromoCoupons.map(c => c.toUpperCase()),
                    Coupons.map(c => c.Code?.toUpperCase()).filter(c => c !== undefined)
                );
                return ArrayExtensions.hasElements(items);
            }
        }
        return false;
    };

    public getProductCodesForCartLines = async () => {
        const {
            data: {
                cart: { result }
            },
            context: { actionContext }
        } = this.props;
        if (!result) {
            return;
        }
        const itemIds = _.uniq(result.cart.CartLines?.map(cl => cl.ItemId).filter(item => item !== undefined) || []);
        const productCodes = await getProductCodesAsync({ callerContext: actionContext }, itemIds as string[]);
        if (productCodes && productCodes.ProductCodes) {
            this.setState({ productCodes: productCodes.ProductCodes });
        }
    };

    public updateAllowableFreightv2 = async (cartId: string | undefined, cartLines: CartLine[]) => {
        if (!cartId) {
            return;
        }
        const actionInput = new GetAllowableFreightCalculationInput(cartId, cartLines);
        const freightCalc = await getAllowableFreightCalculationAction(actionInput, this.props.context.actionContext);
        // const freightCalc = await getAllowableFreightAsync({ callerContext: this.props.context.actionContext }, cartId);
        this.checkAllowableFreightLimit(freightCalc);
        Object.keys(AllowableFreightStatusType).forEach(k => {
            // eslint-disable-next-line security/detect-object-injection
            if (freightCalc?.AllowableFreightStatusLine && freightCalc.AllowableFreightStatusLine.length) {
                // @ts-ignore
                this.applyGlobalState(freightCalc.AllowableFreightStatusLine, AllowableFreightStatusType[k]);
            }
        });
    };

    // public updateAllowableFreight = (cart: Cart) => {
    //     //Check the allowable freight because it has the lowest priority, followed by the item filter.
    //     this.checkAllowableFreightLimit(cart);
    //     Object.keys(AllowableFreightStatusType).forEach(k => {
    //         // eslint-disable-next-line security/detect-object-injection
    //         this.applyGlobalState(cart, AllowableFreightStatusType[k]);
    //     });

    //     // this.applyFreightSliderColor();
    // };

    // private applyFreightSliderColor = () => {
    //     if (this.state.mustShipLTL) {
    //         this.setState({ sliderColourClass: AllowableFreightProgressBarClassEnum.Red });
    //         return;
    //     } else if (this.state.containsOil || this.state.oversizedItem) {
    //         this.setState({ sliderColourClass: AllowableFreightProgressBarClassEnum.Yellow });
    //         return;
    //     } else if (this.state.limitReached) {
    //         this.setState({ sliderColourClass: AllowableFreightProgressBarClassEnum.Green });
    //         return;
    //     }
    // };

    private checkAllowableFreightLimit = (freightCalculation: IAllowableFreightCalculation) => {
        if (!freightCalculation) {
            return;
        }
        const maxAllowableFreight = freightCalculation.ThresholdAmountTotal;

        const isUnitCount = freightCalculation.IsUnitCount;

        const cartFreight = freightCalculation.ThresholdAmountCurrent;

        this.setState({
            cartFreight: parseInt((cartFreight as unknown) as string, 10),
            maxLimit: parseInt((maxAllowableFreight as unknown) as string, 10)
        });
        if (cartFreight >= maxAllowableFreight) {
            this.setState({
                limitReached: true,
                sliderColourClass: AllowableFreightProgressBarClassEnum.Red,
                errorMessage: this.props.resources.freightAllowanceLimitMessage,
                isUnitCount: isUnitCount || false
            });
        } else {
            this.setState({
                limitReached: false,
                sliderColourClass: AllowableFreightProgressBarClassEnum.Red,
                errorMessage: undefined,
                isUnitCount: isUnitCount || false
            });
        }
    };

    private applyGlobalState = (statusLine: IAllowableFreightStatusLine[], key: AllowableFreightStatusType) => {
        const hasStatus = statusLine?.filter(f => f.StatusType === key) || false;
        if (hasStatus.length > 0) {
            switch (key) {
                //Highest priority
                case AllowableFreightStatusType.LTL:
                    this.setState({
                        mustShipLTL: true,
                        errorMessage: this.props.resources.mustShipLTLMessage
                    });
                    break;
                case AllowableFreightStatusType.Oil:
                    this.setState({
                        containsOil: true,
                        errorMessage: this.props.resources.containsOilMessage
                    });
                    break;
                case AllowableFreightStatusType.Oversized:
                    this.setState({
                        oversizedItem: true,
                        errorMessage: this.props.resources.overSizedItemMessage
                    });
                    break;
            }
        }
    };

    public render(): JSX.Element | null {
        const viewProps: IAllowableFreightViewProps = {
            ...this.props,
            state: this.state
        };
        return this.props.renderView(viewProps);
    }
}

export default AllowableFreight;
